/** Gantt zoom and pan. Kept apart from board state: it is per-person view, not shared data. */
import { reactive, ref, watch } from "vue";
import { defineStore } from "pinia";
import { MAX_PX_PER_HOUR, MIN_PX_PER_HOUR, zoomAbout } from "@shared/axis.js";
import { openWindow } from "../lib/windows.js";
import { DEFAULT_SETTINGS, VIEWS, pinKey, type Pin, type SwitcherView, type UserSettings } from "@shared/settings.js";
import { api } from "../lib/api.js";
import { setTheme } from "../lib/theme.js";

/**
 * The whole-screen views, in the order the switcher lists them.
 *
 * `board` is the tree and the timeline side by side; `timeline` is that same
 * chart with the tree's width given back to it, which is also how a phone —
 * where the two cannot share a screen — gets at it.
 */
export { VIEWS };
/** `profile` is a view too, but not one the switcher lists: the status dot in
 *  the topbar is the way in. Global settings is not a view but a window
 *  (lib/windows.ts); an old `?view=settings` link opens it. */
export type View = SwitcherView | "profile";

export const VIEW_LABELS: Record<View, string> = {
  board: "Board",
  timeline: "Timeline",
  calendar: "Calendar",
  messages: "Messages",
  shopping: "Shopping",
  money: "Money",
  repos: "Repos",
  automation: "Automation",
  tmux: "tmux",
  team: "Settings",
  profile: "Profile",
};

export const useViewport = defineStore("viewport", () => {
  /** Left edge of the chart, in hours from now. Starts slightly in the past so
   *  the now-line is visible rather than pinned to the very edge. */
  const h0 = ref(-6);
  const pxPerHour = ref(24);
  /** Hide system events in the task timeline, leaving just what people wrote.
   *  On by default: the comments are what you open a task to read.
   *  Lives here rather than in the drawer so it survives switching tasks — it
   *  is a preference about reading, not a property of one task. */
  const commentsOnly = ref(true);
  /** Drop finished tasks from the tree and gantt, and from a drawer's sub-task
   *  list. Two switches, because tidying the board and checking what is left
   *  under one task are different questions. The board's is on by default:
   *  what is left to do is what you open it to see. */
  const hideDone = ref(true);
  const hideDoneSubtasks = ref(false);
  /** Cut the tree down to the reader's own tasks — assigned to them, or
   *  personal — keeping the parents they sit in for context. */
  const onlyMine = ref(false);
  /** Sibling order: the hand-dragged ranks, or most urgent first. Separate from
   *  grouping, so "by repo" and "by urgency" combine rather than compete. */
  const sortBy = ref<"manual" | "urgency">("manual");
  /** Repo headings in the order the reader dragged them into (`""` = no repo).
   *  Theirs alone: somebody else's arrangement must not move under them. */
  const repoOrder = ref<string[]>([]);
  /** The team on screen, as saved: an id, `"personal"` for the reader's own
   *  tasks, or `""` for their first team. Read `board.teamId` for the team
   *  actually shown: this may name one the reader has left. */
  const team = ref("");
  /** Shared vertical scroll: the tree and the gantt are two views of one list,
   *  so they scroll as one surface. */
  const scrollTop = ref(0);
  // ── views ──────────────────────────────────────────────────────────────
  /** Which whole-screen view is up. One switch for all of them, rather than a
   *  pair of tabs for the panes and a separate toggle for the message panel:
   *  they are all just places to be, and only one of them is ever on screen. */
  const view = ref<View>("board");
  /** The session the tmux view should open on next, then cleared — set by a
   *  task's Claude button after it starts one. Not saved, not in the URL. */
  const tmuxSession = ref<string | null>(null);
  /** A pinned session or queued request a topbar pill asked the tmux view for.
   *  Unlike `tmuxSession` it is taken by the next list whether it is there or
   *  not: a pinned name that has ended must not wait for tmux to reuse it. */
  const tmuxPick = ref<{ session: string } | { queued: string } | { files: { repo: string; worktree: string | null; path: string | null } } | null>(null);
  /** What the tmux view has on screen, while it is up — for the pill that is
   *  "current". Written by TmuxView alone. */
  const tmuxShown = ref<{ session: string | null; queued: string | null } | null>(null);
  /** The file editor's tree and open file, while it is up, and whether that
   *  file has unsaved changes. Written by FilesView alone. */
  const filesShown = ref<{ repo: string; worktree: string | null; path: string | null; dirty: boolean } | null>(null);
  /** People whose entries are hidden. Hidden rather than shown, so someone who
   *  joins the team appears on everyone's calendar instead of being invisible
   *  until each person thinks to tick them. */
  const calendarHidden = reactive(new Set<string>());

  function togglePerson(userId: string): void {
    if (calendarHidden.has(userId)) calendarHidden.delete(userId);
    else calendarHidden.add(userId);
  }

  // ── settings ───────────────────────────────────────────────────────────
  /** The preferences above that are saved against the person, not the tab.
   *  Changing one anywhere — the pane header, the drawer, the profile page —
   *  saves it, so the profile page is only a second place to reach them. */
  const startView = ref<UserSettings["startView"]>(DEFAULT_SETTINGS.startView);
  /** Light, dark, or whatever the device says. Painted by lib/theme.ts. */
  const theme = ref<UserSettings["theme"]>(DEFAULT_SETTINGS.theme);
  watch(theme, setTheme, { flush: "sync" });
  /** What is pinned to the topbar as pills (`PinnedViews.vue`), in pinning order. */
  const pins = ref<Pin[]>([...DEFAULT_SETTINGS.pins]);
  const saved = { startView, hideDone, hideDoneSubtasks, commentsOnly, onlyMine, sortBy, repoOrder, team, theme, pins };

  function isPinned(pin: Pin): boolean {
    const key = pinKey(pin);
    return pins.value.some((p) => pinKey(p) === key);
  }

  function togglePin(pin: Pin): void {
    const key = pinKey(pin);
    pins.value = isPinned(pin) ? pins.value.filter((p) => pinKey(p) !== key) : [...pins.value, pin];
  }

  /** Put `next` where `pin` was, if it is pinned — a queued prompt that was
   *  sent from here becomes its session, in the same place in the row. */
  function replacePin(pin: Pin, next: Pin): void {
    const key = pinKey(pin);
    const at = pins.value.findIndex((p) => pinKey(p) === key);
    if (at < 0) return;
    const nextKey = pinKey(next);
    pins.value = pins.value.flatMap((p, i) => (i === at ? [next] : pinKey(p) === nextKey ? [] : [p]));
  }

  /** Drop the pins `gone` says no longer lead anywhere — a session that ended,
   *  a queued request that was sent. Writes nothing when none are. */
  function dropPins(gone: (p: Pin) => boolean): void {
    if (pins.value.some(gone)) pins.value = pins.value.filter((p) => !gone(p));
  }
  let applying = false;
  let pending: Partial<UserSettings> = {};
  let flushTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * The view on screen is kept in the address as `?view=`, so a reload or a
   * copied link comes back to it, and a tapped notification can name one
   * (`?view=messages`). Only ever REPLACED, never pushed: switching views adds
   * no history, so back keeps meaning what it did — close the task drawer
   * (lib/taskRoute.ts), then leave. That also keeps whatever `history.state`
   * the entry carries, the drawer's `todosPushed` marker included.
   *
   * Not written until the settings have been applied: before then `view` is
   * only the default, and writing it would overwrite the view being linked to.
   */
  let viewInUrl = false;

  function linkedView(): View | null {
    const named = new URL(location.href).searchParams.get("view");
    return named === "profile" || (VIEWS as readonly (string | null)[]).includes(named) ? (named as View) : null;
  }

  /** Put the current view in the address, if it is not there already. Also
   *  called after a popstate: the entry stepped back to holds the view it had
   *  when it was left, and the view does not follow history. */
  function syncViewToUrl(): void {
    if (!viewInUrl) return;
    const url = new URL(location.href);
    if (url.searchParams.get("view") === view.value) return;
    url.searchParams.set("view", view.value);
    history.replaceState(history.state, "", url.pathname + url.search + url.hash);
  }
  watch(view, syncViewToUrl, { flush: "sync" });

  /** A team named in the address, from a tapped notification — taken once and
   *  dropped, so it does not outrank the team picker on a reload. */
  function linkedTeam(): string | null {
    const url = new URL(location.href);
    const named = url.searchParams.get("team");
    if (named === null) return null;
    url.searchParams.delete("team");
    history.replaceState(history.state, "", url.pathname + url.search + url.hash);
    return named;
  }

  /** Take the signed-in person's settings, and open on their start view. */
  function applySettings(settings: UserSettings): void {
    applying = true;
    for (const key of Object.keys(saved) as (keyof UserSettings)[]) {
      (saved[key] as { value: unknown }).value = settings[key];
    }
    // Even when `theme` did not change: this browser may have painted another.
    setTheme(settings.theme);
    view.value = linkedView() ?? settings.startView;
    // Global settings was a view; a link to it (a bookmark, a feedback task's URL) opens its window.
    if (new URL(location.href).searchParams.get("view") === "settings") openWindow({ kind: "settings" });
    team.value = linkedTeam() ?? settings.team;
    applying = false;
    viewInUrl = true;
    syncViewToUrl();
  }

  async function flush(): Promise<void> {
    flushTimer = null;
    const patch = pending;
    pending = {};
    // Only the fields that changed: another tab changing a different one
    // must not be overwritten by this tab's stale copy of it.
    try {
      await api.patch("/api/me/settings", patch);
    } catch (err) {
      console.error("saving settings failed", err);
    }
  }

  for (const key of Object.keys(saved) as (keyof UserSettings)[]) {
    watch(saved[key], (value) => {
      if (applying) return;
      (pending as Record<string, unknown>)[key] = value;
      // Debounced: a run of checkbox clicks is one write, not one each.
      if (flushTimer) clearTimeout(flushTimer);
      flushTimer = setTimeout(() => void flush(), 400);
      // Sync, so `applying` is still set when a load fires it: loading the
      // settings must not write them straight back.
    }, { flush: "sync" });
  }

  function zoom(factor: number, cursorX: number): void {
    const next = zoomAbout({ h0: h0.value, pxPerHour: pxPerHour.value, cursorX, factor });
    h0.value = next.h0;
    pxPerHour.value = next.pxPerHour;
  }

  function pan(dxPx: number): void {
    h0.value -= dxPx / pxPerHour.value;
  }

  function jumpToNow(widthPx: number): void {
    h0.value = -(widthPx / pxPerHour.value) * 0.15;
  }

  function setZoom(next: number): void {
    pxPerHour.value = Math.min(MAX_PX_PER_HOUR, Math.max(MIN_PX_PER_HOUR, next));
  }

  return {
    h0, pxPerHour, commentsOnly, hideDone, hideDoneSubtasks, onlyMine, sortBy, repoOrder, team, scrollTop,
    zoom, pan, jumpToNow, setZoom,
    view, tmuxSession, calendarHidden, togglePerson, syncViewToUrl,
    startView, theme, pins, isPinned, togglePin, replacePin, dropPins, tmuxPick, tmuxShown, filesShown, applySettings,
  };
});
